import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Search, FileText, Link2, Radio, Globe, Package,
  Layers, Tag, GitCompare, Activity, Database, Newspaper, BookOpen
} from 'lucide-react';
import { CATALOG, ARTICLES } from '../help/articles';
import { inline } from '../help/inlineFormat';

// slug → nav icon (matches the icons used in App.jsx NAV_ITEMS)
const ICONS = {
  'ads-txt': FileText,
  'sellers-json': Search,
  'schain': Link2,
  'bid-request': Radio,
  'crawler': Globe,
  'bundles': Package,
  'bundle-lookup': Layers,
  'app-category': Tag,
  'discrepancy': GitCompare,
  'disc-monitor': Activity,
  'fetch-engine': Database,
  'industry-pulse': Newspaper,
};

const ALL = CATALOG.flatMap(g => g.items);

function Article({ slug }) {
  const meta = ALL.find(i => i.slug === slug);
  const data = ARTICLES[slug];

  if (!data) {
    return (
      <div className="pb-empty">
        <div className="em-ic">📝</div>
        <div className="em-t">{meta ? meta.label : 'This tool'} — article in progress</div>
        <div>This page is being written. Check back shortly.</div>
      </div>
    );
  }

  return (
    <>
      <div className="pb-art-head">
        <div className="pb-art-meta">
          <span className="badge badge-cyan">Playbook</span>
          {meta?.tier === 'free'
            ? <span className="badge badge-muted">Free tool</span>
            : <span className="badge badge-amber">Pro</span>}
          {meta?.beta && <span className="badge badge-purple">BETA</span>}
        </div>
        <h1 className="pb-art-title">{data.title}</h1>
        <p className="pb-art-summary">{inline(data.summary)}</p>
      </div>

      {data.sections && data.sections.map((s, i) => {
        if (s.callout) {
          return (
            <div className="pb-callout" key={i}><b>Gotcha — </b>{inline(s.callout)}</div>
          );
        }
        return (
          <section className="pb-sec" key={i}>
            {s.h && <h3>{s.h}</h3>}
            {s.p && s.p.map((p, j) => <p key={j}>{inline(p)}</p>)}
            {s.list && (
              <ul>{s.list.map((li, j) => <li key={j}>{inline(li)}</li>)}</ul>
            )}
            {s.steps && (
              <div className="pb-steps">
                {s.steps.map((st, j) => (
                  <div className="pb-step" key={j}><div className="st">{inline(st)}</div></div>
                ))}
              </div>
            )}
            {s.table && (
              <table className="data-table">
                <thead>
                  <tr>{s.table.head.map((h, j) => <th key={j}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {s.table.rows.map((r, j) => (
                    <tr key={j}>
                      {r.map((c, k) => (
                        <td key={k} style={k === 0 ? { color: 'var(--text-primary)', fontWeight: 500 } : undefined}>
                          {inline(c)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        );
      })}

      {data.faqs && data.faqs.length > 0 && (
        <section className="pb-sec">
          <h3>Common questions &amp; gotchas</h3>
          {data.faqs.map((f, i) => (
            <div className="pb-faq" key={i}>
              <div className="q">{inline(f.q)}</div>
              <div className="a">{inline(f.a)}</div>
            </div>
          ))}
        </section>
      )}
    </>
  );
}

export default function Playbook() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const activeSlug = slug || ALL[0].slug;

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return CATALOG;
    return CATALOG
      .map(g => ({ ...g, items: g.items.filter(it => it.label.toLowerCase().includes(needle)) }))
      .filter(g => g.items.length > 0);
  }, [q]);

  return (
    <div>
      <h1 className="page-title">Playbook</h1>
      <p className="page-subtitle">How each tool works, when to reach for it, and the gotchas worth knowing.</p>

      <div className="pb-grid">
        <aside className="pb-rail">
          <div className="pb-search">
            <Search size={13} />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search the Playbook…"
            />
          </div>
          <nav className="pb-list">
            {groups.length === 0 && (
              <div style={{ padding: '18px', color: 'var(--text-muted)', fontSize: '12px' }}>
                No tools match “{q}”.
              </div>
            )}
            {groups.map(g => (
              <div key={g.group}>
                <div className="pb-group">{g.group}</div>
                {g.items.map(it => {
                  const Icon = ICONS[it.slug] || BookOpen;
                  const isActive = it.slug === activeSlug;
                  return (
                    <a
                      key={it.slug}
                      className={`pb-item ${isActive ? 'active' : ''}`}
                      onClick={() => navigate(`/playbook/${it.slug}`)}
                    >
                      <Icon size={15} className="ico" />
                      <span className="lbl">{it.label}</span>
                      {it.beta && <span className="pb-mini beta">BETA</span>}
                    </a>
                  );
                })}
              </div>
            ))}
          </nav>
        </aside>

        <main className="pb-article">
          <Article slug={activeSlug} />
        </main>
      </div>
    </div>
  );
}
